import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ResetIcon from '@mui/icons-material/RestartAlt';
import SubjectRequirementDialog from './SubjectRequirementDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import licenseComplianceService from '../../services/licenseComplianceService';
import type {
  ConditionalSubjectSuggestionDto,
  LicenseSetupEntryDto,
  SubjectRequirementDto,
  UpsertSubjectRequirementDto,
} from '../../services/licenseComplianceService';
import { getApiErrorInfo } from '../../services/api';

interface SubjectRequirementsListProps {
  entry: LicenseSetupEntryDto;
  /** Called after any successful mutation so the parent can refresh state. */
  onSaved: () => void | Promise<void>;
}

function formatTarget(req: Pick<SubjectRequirementDto, 'targetKind' | 'categoryNames'>): string {
  switch (req.targetKind) {
    case 'total':
      return 'Any subject';
    case 'technical':
      return 'Any technical subject';
    case 'non_technical':
      return 'Any non-technical subject';
    case 'manual':
      return 'Checked off manually from the Reporting page';
    default:
      return req.categoryNames.join(', ');
  }
}

function formatScope(scope: SubjectRequirementDto['scope']): string {
  return scope === 'per_year' ? 'each year' : 'entire period';
}

/**
 * Subject requirements for one license: tracked list with edit/delete,
 * conditional opt-in suggestions, add button, and reset-to-state-defaults.
 */
export default function SubjectRequirementsList({ entry, onSaved }: SubjectRequirementsListProps) {
  const [subjectDialogOpen, setSubjectDialogOpen] = useState(false);
  const [editingRequirement, setEditingRequirement] = useState<SubjectRequirementDto | null>(null);
  const [deletingRequirement, setDeletingRequirement] = useState<SubjectRequirementDto | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [trackingSuggestion, setTrackingSuggestion] = useState<string | null>(null);
  const [suggestionError, setSuggestionError] = useState<string | null>(null);

  // Hide suggestions the user is already tracking (matched by name, however sourced)
  const visibleSuggestions = entry.conditionalSubjectSuggestions.filter(
    (s) =>
      !entry.subjectRequirements.some(
        (r) => r.name.trim().toLowerCase() === s.name.trim().toLowerCase()
      )
  );

  async function handleSubjectSubmit(dto: UpsertSubjectRequirementDto) {
    // Guard against duplicate rows: an add (or a rename onto an existing row)
    // with a name already in the list would render two identical-looking rows.
    const newName = dto.name?.trim();
    const duplicate = newName
      ? entry.subjectRequirements.find(
          (r) =>
            r.id !== editingRequirement?.id &&
            r.name.trim().toLowerCase() === newName.toLowerCase()
        )
      : undefined;
    if (duplicate) {
      throw new Error(`A requirement named "${newName}" already exists for this license.`);
    }
    try {
      if (editingRequirement) {
        await licenseComplianceService.updateSubjectRequirement(
          entry.licenseId,
          editingRequirement.id,
          dto
        );
      } else {
        await licenseComplianceService.addSubjectRequirement(entry.licenseId, dto);
      }
    } catch (error) {
      // Re-throw with a friendly message for the dialog to display
      throw new Error(getApiErrorInfo(error).message, { cause: error });
    }
    await onSaved();
  }

  async function handleDeleteSubject() {
    if (!deletingRequirement) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await licenseComplianceService.deleteSubjectRequirement(
        entry.licenseId,
        deletingRequirement.id
      );
      setDeletingRequirement(null);
      await onSaved();
    } catch (error) {
      setDeleteError(getApiErrorInfo(error).message);
    } finally {
      setDeleting(false);
    }
  }

  async function handleTrackSuggestion(suggestion: ConditionalSubjectSuggestionDto) {
    setTrackingSuggestion(suggestion.name);
    setSuggestionError(null);
    try {
      await licenseComplianceService.addSubjectRequirement(entry.licenseId, {
        name: suggestion.name,
        targetKind: suggestion.targetKind,
        scope: suggestion.scope,
        requiredHours: suggestion.requiredHours,
        categoryIds: suggestion.targetKind === 'category_set' ? suggestion.categoryIds : [],
      });
      await onSaved();
    } catch (error) {
      setSuggestionError(getApiErrorInfo(error).message);
    } finally {
      setTrackingSuggestion(null);
    }
  }

  async function handleResetToStateDefaults() {
    setResetting(true);
    setResetError(null);
    try {
      await licenseComplianceService.prefillLicense(entry.licenseId);
      setResetConfirmOpen(false);
      await onSaved();
    } catch (error) {
      setResetError(getApiErrorInfo(error).message);
    } finally {
      setResetting(false);
    }
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Subject requirements
        </Typography>
        {entry.hasStateData && (
          <Tooltip title="Replaces state-default subject requirements and credit limits with the latest state data. Rows you added yourself are kept.">
            <Button
              size="small"
              startIcon={<ResetIcon />}
              onClick={() => {
                setResetError(null);
                setResetConfirmOpen(true);
              }}
            >
              Reset to state defaults
            </Button>
          </Tooltip>
        )}
      </Box>

      {entry.subjectRequirements.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          No subject requirements are being tracked for this license.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
          {entry.subjectRequirements.map((req) => (
            <Box
              key={req.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                px: 1.5,
                py: 1,
              }}
            >
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {req.name}
                  </Typography>
                  {req.source === 'state_prefill' ? (
                    <Chip label="State default" size="small" variant="outlined" />
                  ) : (
                    <Chip
                      label="Custom"
                      size="small"
                      sx={{ bgcolor: 'status.modified.bg', color: 'status.modified.fg' }}
                    />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {req.targetKind === 'manual'
                    ? formatTarget(req)
                    : `${req.requiredHours} hrs · ${formatScope(req.scope)} · ${formatTarget(req)}`}
                </Typography>
              </Box>
              <Tooltip title="Edit requirement">
                <IconButton
                  size="small"
                  aria-label={`Edit ${req.name}`}
                  onClick={() => {
                    setEditingRequirement(req);
                    setSubjectDialogOpen(true);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete requirement">
                <IconButton
                  size="small"
                  aria-label={`Delete ${req.name}`}
                  onClick={() => {
                    setDeleteError(null);
                    setDeletingRequirement(req);
                  }}
                  sx={{ '&:hover': { color: 'error.main' } }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}

      {visibleSuggestions.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
          {visibleSuggestions.map((suggestion) => (
            <Alert
              key={`${suggestion.name}|${suggestion.scope}`}
              severity="info"
              sx={{ '& .MuiAlert-message': { width: '100%' } }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {suggestion.targetKind === 'manual'
                  ? `Conditional requirement: ${suggestion.name} · checked off manually`
                  : `Conditional requirement: ${suggestion.name} · ${suggestion.requiredHours} hrs · ${formatScope(suggestion.scope)}`}
              </Typography>
              {suggestion.description && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {suggestion.description}
                </Typography>
              )}
              {suggestion.categoryNames.length > 0 && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                  Credits that count: {suggestion.categoryNames.join(', ')}
                </Typography>
              )}
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={
                  trackingSuggestion === suggestion.name ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <AddIcon />
                  )
                }
                onClick={() => handleTrackSuggestion(suggestion)}
                disabled={trackingSuggestion !== null}
                sx={{ mt: 1 }}
              >
                Track this requirement
              </Button>
            </Alert>
          ))}
          {suggestionError && <Alert severity="error">{suggestionError}</Alert>}
        </Box>
      )}

      <Button
        size="small"
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={() => {
          setEditingRequirement(null);
          setSubjectDialogOpen(true);
        }}
      >
        Add new requirement
      </Button>

      <SubjectRequirementDialog
        open={subjectDialogOpen}
        requirement={editingRequirement}
        onSubmit={handleSubjectSubmit}
        onClose={() => setSubjectDialogOpen(false)}
      />

      <ConfirmDialog
        open={deletingRequirement !== null}
        title="Delete subject requirement?"
        message={`"${deletingRequirement?.name ?? ''}" will no longer be tracked for this license. Credits you've earned are not affected.`}
        loading={deleting}
        error={deleteError}
        onConfirm={handleDeleteSubject}
        onClose={() => setDeletingRequirement(null)}
      />

      <ConfirmDialog
        open={resetConfirmOpen}
        title="Reset to state defaults?"
        message={`State-default subject requirements and credit limits for ${entry.stateName || entry.stateCode} will be replaced with the latest state data, and any edits you made to them will be lost. Requirements and limits you added yourself are kept.`}
        confirmLabel="Reset"
        loading={resetting}
        error={resetError}
        onConfirm={handleResetToStateDefaults}
        onClose={() => setResetConfirmOpen(false)}
      />
    </Box>
  );
}
